import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Download from '@mui/icons-material/Download'
import Close from '@mui/icons-material/Close'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import InsertDriveFile from '@mui/icons-material/InsertDriveFile'
import GraphicEq from '@mui/icons-material/GraphicEq'

import { looksLikeChatAudioAttachment } from 'utils/chatAttachmentLimits'

import ChatMessageAudioPlayer from './ChatMessageAudioPlayer'
import * as S from './styles'

interface Props {
  isDoctor?: boolean
  /** Uma linha quando não houver função (autor desconhecido). */
  nameFallback?: string
  senderRole?: string
  senderName?: string
  message: string
  time: string
  fileUrl?: string
  fileName?: string
  fileSize?: number
  contentType?: string
  /** Twilio: renovar URL pré-assinada no download (evita AccessDenied). */
  messageSid?: string
  getFreshMediaDownloadUrl?: (messageSid: string) => Promise<string | null>
  canDelete?: boolean
  deleting?: boolean
  onDelete?: () => void
}

function formatFileSizeBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** `senderRole` vem como "Doutor(a)" / "Paciente" (Twilio) ou equivalente. */
function displayRoleLabel(senderRole: string): string {
  const r = senderRole.trim().toLowerCase()
  if (r === 'paciente') return 'Paciente'
  if (r.startsWith('doutor')) return 'Profissional de saúde'
  return senderRole.trim()
}

function looksLikeImageAttachment(
  fileUrl: string | undefined,
  fileName: string | undefined,
  contentType: string | undefined
): boolean {
  if (contentType?.trim().toLowerCase().startsWith('image/')) {
    return true
  }
  const n = (fileName || '').toLowerCase()
  if (/\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(n)) {
    return true
  }
  if (
    fileUrl &&
    /\.(jpe?g|png|gif|webp|heic|heif|bmp)(\?|#|$)/i.test(fileUrl)
  ) {
    return true
  }
  return false
}

function isRedundantAttachmentLine(
  message: string,
  fileName: string | undefined
): boolean {
  const t = message.trim()
  if (!t) {
    return true
  }
  if (!t.startsWith('📎')) {
    return false
  }
  if (!fileName) {
    return !t.includes('\n')
  }
  const after = t.replace(/^\s*📎\s+/, '').trim()
  return after === fileName
}

function sanitizeDownloadFilename(name: string): string {
  const cleaned = name.replace(/[/\\?%*:|"<>]/g, '_').trim()
  return cleaned.slice(0, 180) || 'download'
}

const Message: React.FC<Props> = ({
  isDoctor,
  nameFallback,
  senderRole,
  senderName,
  message,
  time,
  fileUrl,
  fileName,
  fileSize,
  contentType,
  messageSid,
  getFreshMediaDownloadUrl,
  canDelete,
  deleting,
  onDelete,
}) => {
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  const displayName =
    (fileName && fileName.trim()) ||
    message.replace(/^\s*📎\s+/, '').trim() ||
    'Arquivo'

  const useTwilioFreshUrl = Boolean(getFreshMediaDownloadUrl && messageSid)

  const resolveOpenableMediaUrl = async (): Promise<string | undefined> => {
    if (useTwilioFreshUrl) {
      try {
        const u = await getFreshMediaDownloadUrl!(messageSid!)
        if (u) return u
      } catch {
        /* ignore */
      }
    }
    return fileUrl ?? undefined
  }

  const triggerMediaDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    const url = await resolveOpenableMediaUrl()
    if (!url) {
      window.alert(
        'Link do arquivo indisponível. Recarregue a página e tente novamente.'
      )
      return
    }
    const safeName = sanitizeDownloadFilename(displayName)
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
      if (!res.ok) throw new Error(String(res.status))
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = safeName
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const hasAttachment =
    (fileName != null && fileName.length > 0) ||
    (fileSize != null && !Number.isNaN(fileSize) && fileSize > 0) ||
    (fileUrl != null && fileUrl.length > 0) ||
    /^\s*📎\s+/.test((message || '').trim())

  const canShowFileCard = Boolean(
    hasAttachment &&
    ((fileName != null && fileName.length > 0) ||
      (fileSize != null && !Number.isNaN(fileSize) && fileSize > 0) ||
      (fileUrl != null && fileUrl.length > 0) ||
      /^\s*📎\s+/.test((message || '').trim()))
  )

  const showCaption =
    (message || '').trim().length > 0 &&
    !isRedundantAttachmentLine(message, fileName)

  const isImage =
    Boolean(fileUrl) &&
    canShowFileCard &&
    looksLikeImageAttachment(fileUrl, fileName, contentType)

  const isAudio =
    canShowFileCard &&
    !isImage &&
    looksLikeChatAudioAttachment(fileUrl, fileName, contentType)

  useEffect(() => {
    if (!imageModalOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setImageModalOpen(false)
        setLightboxSrc(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [imageModalOpen])

  const openLightbox = async () => {
    const u = await resolveOpenableMediaUrl()
    setLightboxSrc(u || fileUrl || null)
    setImageModalOpen(true)
  }

  const showHeader = Boolean(
    (senderRole && senderRole.length > 0) ||
    (senderName && senderName.length > 0) ||
    (nameFallback && nameFallback.length > 0)
  )

  return (
    <S.MessageContainer isDoctor={isDoctor}>
      {showHeader && (
        <S.MessageName isDoctor={isDoctor}>
          {senderRole ? (
            <>
              <S.MessageRoleText>
                {displayRoleLabel(senderRole)}
              </S.MessageRoleText>
              {senderName ? (
                <S.MessageNameParens> ({senderName})</S.MessageNameParens>
              ) : null}
            </>
          ) : (
            <S.MessageNameSingle>
              {(senderName || nameFallback || '').trim() || '—'}
            </S.MessageNameSingle>
          )}
        </S.MessageName>
      )}
      <S.MessageContent
        isDoctor={isDoctor}
        $fitWidth={Boolean(canShowFileCard && isImage && fileUrl)}
      >
        {canDelete && onDelete ? (
          <S.MessageDeleteButton
            type="button"
            isDoctor={isDoctor}
            aria-label="Apagar mensagem"
            title="Apagar mensagem"
            disabled={deleting}
            onClick={() => void onDelete()}
          >
            <DeleteOutline style={{ fontSize: 16 }} />
          </S.MessageDeleteButton>
        ) : null}
        {canShowFileCard && showCaption && (
          <S.MessageCaptionOnly>{message}</S.MessageCaptionOnly>
        )}
        {canShowFileCard && isImage && fileUrl ? (
          <>
            <S.ImageBubbleRoot>
              <S.ImageBubbleFrame>
                <S.ImageBubbleOpenButton
                  type="button"
                  onClick={() => void openLightbox()}
                  aria-label="Ampliar imagem"
                >
                  <S.ImageBubbleImg
                    src={fileUrl}
                    alt={displayName || 'Imagem anexada'}
                  />
                </S.ImageBubbleOpenButton>
              </S.ImageBubbleFrame>
              <S.ImageBubbleMeta isDoctor={isDoctor}>
                {fileSize != null && !Number.isNaN(fileSize) && fileSize > 0 ? (
                  <S.FileSizeLabel>
                    {formatFileSizeBytes(fileSize)}
                  </S.FileSizeLabel>
                ) : null}
              </S.ImageBubbleMeta>
              <S.FileCardFooter isDoctor={isDoctor}>
                <S.FileCardTime>{time}</S.FileCardTime>
              </S.FileCardFooter>
            </S.ImageBubbleRoot>
            {imageModalOpen &&
              createPortal(
                <S.ImageLightboxBackdrop
                  role="presentation"
                  onClick={() => {
                    setImageModalOpen(false)
                    setLightboxSrc(null)
                  }}
                >
                  <S.ImageLightboxPanel
                    role="dialog"
                    aria-modal="true"
                    aria-label="Visualização da imagem"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <S.ImageLightboxToolbar>
                      <S.ImageLightboxTitle>{displayName}</S.ImageLightboxTitle>
                      <S.ImageLightboxDownloadBtn
                        type="button"
                        title="Baixar imagem"
                        aria-label="Baixar imagem"
                        onClick={() => void triggerMediaDownload()}
                      >
                        <Download style={{ fontSize: 22 }} />
                      </S.ImageLightboxDownloadBtn>
                      <S.ImageLightboxIconBtn
                        type="button"
                        onClick={() => {
                          setImageModalOpen(false)
                          setLightboxSrc(null)
                        }}
                        aria-label="Fechar"
                      >
                        <Close style={{ fontSize: 22 }} />
                      </S.ImageLightboxIconBtn>
                    </S.ImageLightboxToolbar>
                    <S.ImageLightboxBody>
                      <S.ImageLightboxImg
                        src={lightboxSrc || fileUrl || ''}
                        alt={displayName || ''}
                      />
                    </S.ImageLightboxBody>
                  </S.ImageLightboxPanel>
                </S.ImageLightboxBackdrop>,
                document.body
              )}
          </>
        ) : null}
        {canShowFileCard && isAudio ? (
          <S.AudioAttachmentBlock>
            <S.FileAttachmentRow>
              <S.FileIconCircle>
                <GraphicEq style={{ fontSize: 22 }} />
              </S.FileIconCircle>
              <S.FileMetaCol>
                <S.FileTitle>
                  {displayName.toLowerCase().includes('audio')
                    ? 'Mensagem de áudio'
                    : displayName}
                </S.FileTitle>
                {fileSize != null && !Number.isNaN(fileSize) && fileSize > 0 ? (
                  <S.FileSizeLabel>
                    {formatFileSizeBytes(fileSize)}
                  </S.FileSizeLabel>
                ) : null}
              </S.FileMetaCol>
            </S.FileAttachmentRow>
            <ChatMessageAudioPlayer
              fileUrl={fileUrl}
              fileName={fileName}
              contentType={contentType}
              messageSid={messageSid}
              getFreshMediaDownloadUrl={getFreshMediaDownloadUrl}
            />
            <S.FileCardFooter isDoctor={isDoctor}>
              <S.FileCardTime>{time}</S.FileCardTime>
            </S.FileCardFooter>
          </S.AudioAttachmentBlock>
        ) : null}
        {canShowFileCard && !isImage && !isAudio ? (
          <S.FileAttachmentBlock>
            <S.FileAttachmentRow>
              <S.FileIconCircle>
                <InsertDriveFile style={{ fontSize: 22 }} />
              </S.FileIconCircle>
              <S.FileMetaCol>
                <S.FileTitle>{displayName}</S.FileTitle>
                {fileSize != null && !Number.isNaN(fileSize) && fileSize > 0 ? (
                  <S.FileSizeLabel>
                    {formatFileSizeBytes(fileSize)}
                  </S.FileSizeLabel>
                ) : null}
              </S.FileMetaCol>
              {fileUrl ? (
                <S.FileDownloadAction
                  as="button"
                  type="button"
                  title="Baixar arquivo"
                  aria-label="Baixar arquivo"
                  onClick={(e) => void triggerMediaDownload(e)}
                >
                  <Download style={{ fontSize: 22 }} />
                </S.FileDownloadAction>
              ) : (
                <S.FileDownloadAction
                  as="span"
                  className="disabled"
                  title="Link indisponível"
                  aria-label="Download indisponível"
                >
                  <Download style={{ fontSize: 22 }} />
                </S.FileDownloadAction>
              )}
            </S.FileAttachmentRow>
            <S.FileCardFooter isDoctor={isDoctor}>
              <S.FileCardTime>{time}</S.FileCardTime>
            </S.FileCardFooter>
          </S.FileAttachmentBlock>
        ) : null}
        {!canShowFileCard && (
          <S.MessageTextBlock isDoctor={isDoctor}>
            <S.MessageTextBody>{message}</S.MessageTextBody>
            <S.MessageTimeInline isDoctor={isDoctor}>
              {time}
            </S.MessageTimeInline>
          </S.MessageTextBlock>
        )}
      </S.MessageContent>
    </S.MessageContainer>
  )
}

export default Message
